import { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { IEpisode, IVideoClip, IVideoClipResource, VideoEditConfig } from '@packages/global';
import '@xyflow/react/dist/style.css';

import { useModels } from '../../../hooks/useModels';
import { useVideoEditor } from '../context/VideoEditorContext';
import ClipCanvasFlow from './ClipCanvasFlow';
import { ClipCanvasProvider } from './ClipCanvasContext';
import ClipCanvasErrorBoundary from './nodes/ClipCanvasErrorBoundary';
import './clipInfiniteCanvas.css';

interface ClipInfiniteCanvasModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoClip: IVideoClip;
  resources: IVideoClipResource[];
  episode?: IEpisode | null;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  onTriggerResourcePoll?: () => Promise<void>;
  editConfig?: VideoEditConfig | null;
  onEditConfigChange?: (config: VideoEditConfig) => void;
}

export default function ClipInfiniteCanvasModal({
  isOpen,
  onClose,
  videoClip,
  resources,
  episode,
  refreshVideoClip,
  onTriggerResourcePoll,
  editConfig,
  onEditConfigChange,
}: ClipInfiniteCanvasModalProps) {
  const { models, loading: modelsLoading } = useModels();
  const { state, actions } = useVideoEditor();
  const [activeClipId, setActiveClipId] = useState(videoClip.id);
  const [clipsRailCollapsed, setClipsRailCollapsed] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveClipId(videoClip.id);
      setClipsRailCollapsed(false);
    }
  }, [isOpen, videoClip.id]);

  const orderedClips = useMemo(() => {
    const clips = state.videoClips.length > 0 ? state.videoClips : [videoClip];
    const clipById = new Map(clips.map((clip) => [clip.id, clip]));
    const orderedFromEditConfig = (editConfig?.clips ?? [])
      .map((clipInfo) => clipById.get(clipInfo.clipId))
      .filter((clip): clip is IVideoClip => Boolean(clip));
    const orderedIds = new Set(orderedFromEditConfig.map((clip) => clip.id));
    const remaining = clips
      .filter((clip) => !orderedIds.has(clip.id))
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    return [...orderedFromEditConfig, ...remaining];
  }, [editConfig?.clips, state.videoClips, videoClip]);

  const activeClip = orderedClips.find((clip) => clip.id === activeClipId) ?? videoClip;
  const activeResources = activeClip.id === videoClip.id
    ? resources
    : actions.getClipResources(activeClip.id);
  const activeClipTitle = activeClip.title || activeClip.sceneName || `Clip #${activeClip.sequence}`;

  const handleSelectClip = (clip: IVideoClip) => {
    setActiveClipId(clip.id);
    actions.selectClip(clip);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="clip-canvas-dialog" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="clip-canvas-dialog__backdrop" />
        </Transition.Child>

        <div className="clip-canvas-dialog__wrap">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Panel className={`clip-canvas-dialog__panel${clipsRailCollapsed ? ' is-clips-collapsed' : ''}`}>
              {(orderedClips.length <= 1 || clipsRailCollapsed) && (
                <Dialog.Title className="clip-canvas-sr-only">
                  {`无限画布 ${activeClipTitle}`}
                </Dialog.Title>
              )}
              <button type="button" className="clip-canvas-close" onClick={onClose} aria-label="关闭">
                <XMarkIcon className="h-5 w-5" />
              </button>

              <div className="clip-canvas-body">
                {orderedClips.length > 1 && (
                  <aside className={`clip-canvas-clip-rail${clipsRailCollapsed ? ' is-collapsed' : ''}`} aria-label="剧集 clips">
                    <header className="clip-canvas-clip-rail__topbar">
                      {!clipsRailCollapsed && (
                        <div className="clip-canvas-clip-rail__heading">
                          <Dialog.Title className="clip-canvas-header__title">
                            无限画布
                          </Dialog.Title>
                          <span className="clip-canvas-header__separator" aria-hidden="true" />
                          <div className="clip-canvas-header__subtitle">
                            {activeClipTitle}
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        className="clip-canvas-clip-rail__toggle"
                        onClick={() => setClipsRailCollapsed((prev) => !prev)}
                        aria-label={clipsRailCollapsed ? '展开 clips' : '收起 clips'}
                        title={clipsRailCollapsed ? '展开 clips' : '收起 clips'}
                      >
                        {clipsRailCollapsed ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />}
                      </button>
                    </header>
                    <div className="clip-canvas-clip-rail__list">
                      {orderedClips.map((clip, index) => {
                        const isActive = clip.id === activeClip.id;
                        const thumbnail = clip.videoUrl || clip.storyboardImage || clip.startImage || clip.endImage;
                        const clipTitle = clip.title || clip.sceneName || `Clip #${clip.sequence || index + 1}`;
                        const hasVideo = Boolean(clip.videoUrl);

                        return (
                          <button
                            key={clip.id}
                            type="button"
                            className={`clip-canvas-clip-card${isActive ? ' is-active' : ''}`}
                            onClick={() => handleSelectClip(clip)}
                            aria-current={isActive ? 'true' : undefined}
                            title={clipTitle}
                          >
                            <span className="clip-canvas-clip-card__thumb" aria-hidden="true">
                              {thumbnail ? (
                                hasVideo ? (
                                  <video src={thumbnail} muted playsInline preload="metadata" />
                                ) : (
                                  <img src={thumbnail} alt="" />
                                )
                              ) : (
                                <span className="clip-canvas-clip-card__empty">暂无</span>
                              )}
                            </span>
                            <span className="clip-canvas-clip-card__meta">
                              <span className="clip-canvas-clip-card__title">
                                <span className="clip-canvas-clip-card__index">#{clip.sequence || index + 1}</span>
                                <span className="clip-canvas-clip-card__title-text">{clipTitle}</span>
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </aside>
                )}
                <div className="clip-canvas-content">
                  <ClipCanvasErrorBoundary key={activeClip.id}>
                    <ClipCanvasProvider
                      videoClip={activeClip}
                      episode={episode ?? null}
                      models={models}
                      modelsLoading={modelsLoading}
                      editConfig={editConfig ?? null}
                      onEditConfigChange={onEditConfigChange}
                      refreshVideoClip={refreshVideoClip}
                      resources={activeResources}
                      isPolling={false}
                      onTriggerResourcePoll={onTriggerResourcePoll}
                    >
                      <ClipCanvasFlow videoClip={activeClip} resources={activeResources} />
                    </ClipCanvasProvider>
                  </ClipCanvasErrorBoundary>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
